import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styles from '../Styles/Auth.module.css';
import { verifyToken } from '../Functions/verifyToken';
import { useAuth } from '../Components/AuthContext';

const EditAccount = () => {
    // State for form fields
    const [userType, setUserType] = useState(''); // GMF or Institution
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [institutionName, setInstitutionName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false,
        hasSpecialChar: false,
    });
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [message, setMessage] = useState('');
    // for validation error messages
    const [error, setError] = useState('');
    // for errors due to submitting to the server
    const [submissionError, setSubmissionError] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const [isLoading, setIsLoading] = useState(true); // Add loading state

    const url = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    const { token, role, user, login, logout } = useAuth();

    const handleDelete = async () => {
        const ok = window.confirm(
            'Are you sure you want to delete your account? This action cannot be undone.'
        );
        if (!ok) return;

        try {
            const endpoint =
                userType === 'GMF'
                    ? `${url}/gmf-employee/${user.id}`
                    : `${url}/institution-account/${user.id}`;

            const resp = await fetch(endpoint, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!resp.ok) {
                const err = await resp.json();
                throw new Error(err.message || 'Failed to delete account.');
            }

            window.alert('Your account has been deleted. Goodbye! 👋');
            logout();
            navigate('/login', { replace: true });
        } catch (e) {
            window.alert(e.message);
        }
    };

    //
    // 1) On mount: verify token + role, then decide userType
    //
    useEffect(() => {
        const initialize = async () => {
            if (!(await verifyToken(token))) {
                return navigate('/login', { replace: true });
            }
            if (!role) {
                return navigate('/login', { replace: true });
            }

            // decide type
            let type;
            if (role === 'GMF_EMPLOYEE') type = 'GMF';
            else if (role === 'INSTITUTION') type = 'Institution';
            else {
                setError('Invalid role detected. Please contact support.');
                return navigate('/login', { replace: true });
            }

            setUserType(type);
            setIsLoggedIn(true);
        };

        initialize();
    }, [token, role, navigate]);

    //
    // 2) Once logged in and userType is set, fetch the details
    //
    useEffect(() => {
        if (!isLoggedIn || !user?.id || !userType) return;

        const fetchUserDetails = async () => {
            try {
                const endpoint =
                    userType === 'GMF'
                        ? `${url}/gmf-employee/${user.id}`
                        : `${url}/institution-account/${user.id}`;

                const resp = await fetch(endpoint, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await resp.json();

                if (!resp.ok) {
                    throw new Error(
                        data.message || 'Failed to fetch account details.'
                    );
                }

                setFirstName(data.first_name || '');
                setLastName(data.last_name || '');
                setInstitutionName(data.institution_name || '');
                setEmail(data.email || '');
            } catch (e) {
                setError(e.message);
                window.alert(
                    "Oops! We couldn't fetch your account details. Please try again later. 😊"
                );
            } finally {
                setIsLoading(false); // Stop loading once the fetch is complete
            }
        };

        fetchUserDetails();
    }, [isLoggedIn, user?.id, userType, token, url]);

    // Validate password as the user types
    const validatePassword = (password) => {
        const requirements = {
            minLength: password.length >= 12,
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        };
        setPasswordRequirements(requirements);
        return Object.values(requirements).every(Boolean);
    };

    // Validate name fields (at least one non-whitespace character, max length 50)
    const validateName = (name) => name.trim().length > 0 && name.length <= 50;

    // Validate email (basic email regex and max length 100)
    const validateEmail = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 100;

    // Handle password input changes
    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        validatePassword(value);
    };

    // Mark password field as touched when it loses focus
    const handlePasswordBlur = () => {
        setPasswordTouched(true);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous error messages
        // use setError for validation errors and setSubmissionError for server-related errors
        setError('');
        setSubmissionError('');

        // Validate all fields before submission
        // only GMF has first name
        if (userType === 'GMF' && !validateName(firstName)) {
            setError('First name is invalid.');
            return;
        }
        // only GMF has last name
        if (userType === 'GMF' && !validateName(lastName)) {
            setError('Last name is invalid.');
            return;
        }
        // only institution has institution name
        if (userType === 'Institution' && !validateName(institutionName)) {
            setError('Institution name is invalid.');
            return;
        }
        if (!validateEmail(email)) {
            setError('Email is invalid.');
            return;
        }
        if (password && password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        try {
            if (!token) {
                throw new Error('No token found. Please log in.');
            }

            const payload = {
                first_name: firstName,
                last_name: lastName,
                email,
            };

            // only include institution_name when relevant:
            if (userType === 'Institution') {
                payload.institution_name = institutionName;
            }

            // only include password when it’s non-empty:
            if (password) {
                payload.password = password;
            }

            const endpoint =
                userType === 'GMF'
                    ? `${url}/gmf-employee/${user.id}`
                    : `${url}/institution-account/${user.id}`;

            // TODO: get rid of this
            console.log('PUT payload:', payload);

            // Send updated account details to the backend
            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (response.ok) {
                setMessage(data.message); // Show success message

                if (email !== user.email) {
                    window.alert(
                        "Your account has been updated successfully! 😊 Please note that you'll need to log in again because of how secure authentication works. Thank you for understanding!"
                    );
                    // anyways forces them to login again after they try to access some pages so might as well send them to logout
                    logout(); // Call the logout function from the AuthContext
                } else {
                    setMessage(data.message); // Show success message
                    window.alert(
                        'Your account has been successfully updated! 🎉😊'
                    );

                    // Update the user context with the new details
                    // Required so Header can show the correct name
                    const updatedUser = {
                        ...user,
                        first_name: firstName,
                        last_name: lastName,
                        email,
                        ...(userType === 'Institution' && {
                            institution_name: institutionName,
                        }),
                    };
                    login(token, role, updatedUser);
                }
                // navigate('/home', { replace: true });
            } else {
                throw new Error(
                    data.message || 'Failed to update account details.'
                );
            }
        } catch (err) {
            setSubmissionError(
                err.message ||
                    'Failed to update account details. Please try again.'
            );
            setMessage('');
        }
    };

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <h1>Loading your account details...</h1>
                <p>Please wait a moment. 😊</p>
            </div>
        );
    }

    return (
        <div className={styles.home}>
            <header className={styles.headerSection}>
                <div className={styles.contentContainer}>
                    <h1 className={styles.title}>Edit Account Details</h1>
                    <p className={styles.instructions}>
                        Update your account details below. Fields marked with an
                        asterisk (*) are required. If you'd like to change your
                        password, you can do so in the optional section below.
                    </p>

                    <form onSubmit={handleSubmit} noValidate>
                        {/* First Name */}
                        {/* First Name (only for GMF users) */}
                        {userType === 'GMF' && (
                            <div className={styles.formGroup}>
                                <label htmlFor="firstName">First Name *</label>
                                <input
                                    id="firstName"
                                    type="text"
                                    value={firstName}
                                    onChange={(e) =>
                                        setFirstName(e.target.value)
                                    }
                                    onBlur={() =>
                                        setError(
                                            !validateName(firstName)
                                                ? 'First name is required and must be less than 50 characters.'
                                                : ''
                                        )
                                    }
                                    required
                                />
                                {!validateName(firstName) && (
                                    <p className={styles.errorMessage}>
                                        First name is required and must be less
                                        than 50 characters.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Last Name (only for GMF users) */}
                        {userType === 'GMF' && (
                            <div className={styles.formGroup}>
                                <label htmlFor="lastName">Last Name *</label>
                                <input
                                    id="lastName"
                                    type="text"
                                    value={lastName}
                                    onChange={(e) =>
                                        setLastName(e.target.value)
                                    }
                                    onBlur={() =>
                                        setError(
                                            !validateName(lastName)
                                                ? 'Last name is required and must be less than 50 characters.'
                                                : ''
                                        )
                                    }
                                    required
                                />
                                {!validateName(lastName) && (
                                    <p className={styles.errorMessage}>
                                        Last name is required and must be less
                                        than 50 characters.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Institution Name (only for Institution users) */}
                        {userType === 'Institution' && (
                            <div className={styles.formGroup}>
                                <label htmlFor="institutionName">
                                    Institution Name *
                                </label>
                                <input
                                    id="institutionName"
                                    type="text"
                                    value={institutionName}
                                    onChange={(e) =>
                                        setInstitutionName(e.target.value)
                                    }
                                    onBlur={() =>
                                        setError(
                                            !validateName(institutionName)
                                                ? 'Institution name is required and must be less than 50 characters.'
                                                : ''
                                        )
                                    }
                                    required
                                />
                                {!validateName(institutionName) && (
                                    <p className={styles.errorMessage}>
                                        Institution name is required and must be
                                        less than 50 characters.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Email */}
                        <div className={styles.formGroup}>
                            <label htmlFor="email">Email *</label>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={() =>
                                    setError(
                                        !validateEmail(email)
                                            ? 'Please enter a valid email address.'
                                            : ''
                                    )
                                }
                                required
                            />
                            {!validateEmail(email) && (
                                <p className={styles.errorMessage}>
                                    Please enter a valid email address.
                                </p>
                            )}
                        </div>

                        {/* Optional Password Section */}
                        <h2 className={styles.subTitle}>
                            Optional: Change Password
                        </h2>
                        <p className={styles.instructions}>
                            If you'd like to update your password, please enter
                            a new password below. Make sure it meets the
                            requirements.
                        </p>

                        {/* Password */}
                        <div className={styles.formGroup}>
                            <label htmlFor="password">New Password</label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={handlePasswordChange}
                                onBlur={handlePasswordBlur}
                            />
                            <ul className={styles.passwordRequirements}>
                                <li
                                    className={
                                        passwordRequirements.minLength
                                            ? styles.valid
                                            : passwordTouched
                                            ? styles.invalid
                                            : styles.defaultText
                                    }
                                >
                                    At least 12 characters
                                </li>
                                <li
                                    className={
                                        passwordRequirements.hasSpecialChar
                                            ? styles.valid
                                            : passwordTouched
                                            ? styles.invalid
                                            : styles.defaultText
                                    }
                                >
                                    At least one special character (one of the
                                    following: !@#$%^&*(),.?":{}|<></>) - don't
                                    be predictable and put it at the end!
                                </li>
                            </ul>
                        </div>

                        {/* Confirm Password */}
                        <div className={styles.formGroup}>
                            <label htmlFor="confirmPassword">
                                Confirm New Password
                            </label>
                            <input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                            />
                            {confirmPassword &&
                                confirmPassword !== password && (
                                    <p className={styles.errorMessage}>
                                        Passwords do not match.
                                    </p>
                                )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            title="Submit these account changes to the server and overwrite previous details"
                            className={styles.submitButton}
                            disabled={
                                // only GMF has first and last name
                                (userType === 'GMF' &&
                                    (!validateName(firstName) ||
                                        !validateName(lastName))) ||
                                // only institution has institution name
                                (userType === 'Institution' &&
                                    !validateName(institutionName)) ||
                                !validateEmail(email) ||
                                (password &&
                                    (!passwordRequirements.minLength ||
                                        !passwordRequirements.hasSpecialChar ||
                                        confirmPassword !== password))
                            }
                        >
                            Update Account
                        </button>
                        <button
                            type="button"
                            className={styles.deleteButton}
                            onClick={handleDelete}
                            title="Permanently delete your account"
                        >
                            Delete Account
                        </button>
                    </form>

                    {/* Success or Error Messages */}
                    {message && (
                        <p className={styles.linkText}>
                            {message} 🎉 You can continue using your account
                            with the updated details.
                        </p>
                    )}
                    {/* Render submission errors only for server-related issues */}
                    {submissionError && (
                        <p className={styles.errorMessage}>
                            {submissionError} Please try again or contact
                            support if the issue persists.
                        </p>
                    )}

                    {/* Render input validation errors inline */}
                    {error && <p className={styles.errorMessage}>{error}</p>}

                    {/* Link back to login if not logged in */}
                    {!isLoggedIn && (
                        <p className={styles.linkText}>
                            Remembered your password?{' '}
                            <Link to="/login">Login</Link>.
                        </p>
                    )}
                </div>
            </header>
        </div>
    );
};

export default EditAccount;
